import { dirname } from 'path';
import { WebGLRenderer, LoadingManager, DefaultLoadingManager } from 'three';
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader.js';
import { GLTFLoader as BaseGLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { KTX2Loader } from 'three/examples/jsm/loaders/KTX2Loader.js';

import { MSFTLod, SLOPEMetadata } from '../../plugins/index.js';

export type { GLTF } from 'three/examples/jsm/loaders/GLTFLoader.js';

export interface GLTFLoaderOptions {
  draco: boolean;
  ktx2: boolean;
}

/**
 * ### Slope GLTF Loader
 *
 * This is a wrapper around the Three.js GLTFLoader that adds support for
 * SLOPECourse and other expected extensions.
 *
 *
 * #### Supported Extensions
 *
 * - KHR_binary_gltf
 * - KHR_draco_mesh_compression
 * - KHR_draco_mesh_compression
 * - KHR_materials_ior
 * - KHR_materials_specular
 * - KHR_materials_transmission
 * - KHR_materials_iridescence
 * - KHR_materials_unlit
 * - KHR_materials_volume
 * - KHR_mesh_quantization
 * - KHR_lights_punctual (partial)
 * - KHR_texture_basisu
 * - KHR_texture_transform
 * - EXT_texture_webp
 * - EXT_meshopt_compression
 * - EXT_mesh_gpu_instancing
 *
 * #### Additional Extensions
 *
 * - SLOPE_course
 * - KHR_environment_map
 * - MSFT_lod
 *
 * #### Pending to implement
 *
 * - KHR_materials_variants
 * - KHR_materials_emissive_strength
 */

export class GLTFLoader extends BaseGLTFLoader {
  private options: GLTFLoaderOptions;

  constructor(manager: LoadingManager = DefaultLoadingManager, options?: GLTFLoaderOptions) {
    super(manager);
    this.options = {
      draco: true,
      ktx2: true,
      ...options,
    };

    this.register((parser) => new SLOPEMetadata(parser));

    this.register(
      (parser) =>
        new MSFTLod(parser, {
          loadingMode: 'all',
        }),
    );

    if (this.options.draco) {
      const [dracoWrapper] = [
        new URL('three/examples/jsm/libs/draco/draco_wasm_wrapper.js', import.meta.url),
        new URL('three/examples/jsm/libs/draco/draco_decoder.wasm', import.meta.url),
      ];

      const dracoLoader = new DRACOLoader(this.manager);
      dracoLoader.setDecoderConfig({ type: 'wasm' });
      dracoLoader.setDecoderPath(`${dirname(dracoWrapper.toString())}/`);

      this.setDRACOLoader(dracoLoader);
    }

    if (this.options.ktx2) {
      const [ktx2Wrapper] = [
        new URL('three/examples/jsm/libs/basis/basis_transcoder.js', import.meta.url),
        new URL('three/examples/jsm/libs/basis/basis_transcoder.wasm', import.meta.url),
      ];

      const ktx2Loader = new KTX2Loader(this.manager);
      ktx2Loader.setTranscoderPath(`${dirname(ktx2Wrapper.toString())}/`);

      this.setKTX2Loader(ktx2Loader);
    }

    this.setCrossOrigin('anonymous');
  }

  detectSupport(renderer: WebGLRenderer): this {
    GLTFLoader.detectSupport(renderer);
    return this;
  }

  static detectSupport(renderer: WebGLRenderer) {
    new KTX2Loader().detectSupport(renderer);
  }
}

export default GLTFLoader;
